/**
 * Custom hook for managing landingPage data (header, footer, site settings)
 * Provides cached access to dynamic content from Strapi
 */

import { useEffect, useState } from 'react';

import {
  CTA,
  fetchLandingPageData,
  LandingPageData,
  LandingPageServiceItem,
  NavigationItem,
  PartnerItem,
  SocialLink,
  StrapiImage,
} from '@/services';
import { getImageUrl } from '@/services/utils';

interface UseLandingPageDataReturn {
  landingPageData: LandingPageData | null;
  loading: boolean;
  error: string | null;
  // Site information
  siteData: {
    siteName: string;
    siteDescription: string;
    favicon?: string;
  };
  // SEO data
  seoData: {
    metaTitle: string;
    metaDescription: string;
    keywords?: string;
    metaImage?: string;
  };
  // Header data
  headerData: {
    logo?: StrapiImage | null;
    navigationItems: NavigationItem[];
    showSearch: boolean;
    searchPlaceholder: string;
  };
  // Footer data
  footerData: {
    companyName: string;
    companyDescription: string;
    contactInfo?: {
      address: string;
      phone: string;
      email: string;
    };
    socialLinks: SocialLink[];
    footerSections: Array<{
      title: string;
      links: Array<{
        label: string;
        href: string;
      }>;
    }>;
    copyright: string;
  };
  // Banner collection data
  bannerCollectionData: {
    banners: Array<{
      id: number;
      title: string;
      subtitle: string;
      backgroundImage?: StrapiImage;
      ctaText: string;
      ctaLink: string;
      showNavigation: boolean;
      showMouseIndicator: boolean;
      overlayOpacity: number;
      order: number;
      isActive: boolean;
    }>;
    autoPlay: boolean;
    autoPlayInterval: number;
    showNavigation: boolean;
    showMouseIndicator: boolean;
  };
  // About section data
  aboutSectionData: {
    title: string;
    content: string;
    buttonText: string;
    buttonLink: string;
    image?: StrapiImage;
  };
  // Service section data
  serviceSectionData: {
    title: string;
    subtitle: string;
    backgroundImage?: StrapiImage;
    services: LandingPageServiceItem[];
    cta?: CTA;
  };
  // Partners section data
  partnersSectionData: {
    id: number;
    title: string;
    subtitle?: string;
    partners: PartnerItem[];
    autoPlay?: boolean;
    autoPlayInterval?: number;
  };
  // Featured projects section data
  featuredProjectsSectionData: {
    id: number;
    title: string;
    projects: Array<{
      id: number;
      title: string;
      description?: string;
      image?: StrapiImage;
      order?: number;
    }>;
  };
  // Featured news section data
  featuredNewsSectionData: {
    id: number;
    title: string;
    subtitle: string;
    viewMoreText: string;
    viewMoreLink: string;
    articles: Array<{
      id: number;
      documentId: string;
      title: string;
      slug: string;
      excerpt: string;
      content: string;
      author: string;
      publishedDate: string;
      readingTime: number;
      featured: boolean;
      tags: string[];
      createdAt: string;
      updatedAt: string;
      publishedAt: string;
      locale: string | null;
      featuredImage?: StrapiImage;
    }>;
  };
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage landingPage data from Strapi
 * @returns Object with landingPage data, loading state, error state, and refetch function
 */
export function useLandingPageData(): UseLandingPageDataReturn {
  const [landingPageData, setLandingPageData] =
    useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch landingPage data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchLandingPageData();
      setLandingPageData(data);
    } catch (err) {
      console.error('Error fetching landingPage data:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to fetch landingPage data'
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      fetchData();
    } else {
      // In SSR, just set loading to false
      setLoading(false);
    }
  }, []);

  // Header and footer data are now fetched separately
  const headerData = {
    logo: undefined,
    navigationItems: [],
    showSearch: false,
    searchPlaceholder: '',
  };

  // Transform site data
  const siteData = {
    siteName: landingPageData?.siteName ?? '',
    siteDescription: landingPageData?.siteDescription ?? '',
    favicon: landingPageData?.favicon?.url
      ? getImageUrl(landingPageData.favicon)
      : '',
  };

  // Transform SEO data
  const seoData = {
    metaTitle: landingPageData?.defaultSeo?.metaTitle ?? '',
    metaDescription: landingPageData?.defaultSeo?.metaDescription ?? '',
    keywords: landingPageData?.defaultSeo?.keywords ?? undefined,
    metaImage: '', // Remove metaImage reference since it doesn't exist in the type
  };

  // Footer data is now fetched separately
  const footerData = {
    companyName: '',
    companyDescription: '',
    contactInfo: undefined,
    socialLinks: [],
    footerSections: [],
    copyright: '',
  };

  // Transform banner collection data
  const bannerCollectionData = {
    banners:
      landingPageData?.bannerCollection?.banners?.map(banner => ({
        id: banner.id,
        title: banner.title,
        subtitle: banner.subtitle,
        backgroundImage: banner.backgroundImage,
        ctaText: banner.ctaText,
        ctaLink: banner.ctaLink,
        showNavigation: banner.showNavigation,
        showMouseIndicator: banner.showMouseIndicator,
        overlayOpacity: banner.overlayOpacity,
        order: banner.order,
        isActive: banner.isActive,
      })) ?? [],
    autoPlay: landingPageData?.bannerCollection?.autoPlay ?? false,
    autoPlayInterval:
      landingPageData?.bannerCollection?.autoPlayInterval ?? 5000,
    showNavigation: landingPageData?.bannerCollection?.showNavigation ?? true,
    showMouseIndicator:
      landingPageData?.bannerCollection?.showMouseIndicator ?? true,
  };

  const aboutSectionData = {
    title: landingPageData?.aboutSection?.title ?? '',
    content: landingPageData?.aboutSection?.content ?? '',
    buttonText: landingPageData?.aboutSection?.buttonText ?? '',
    buttonLink: landingPageData?.aboutSection?.buttonLink ?? '',
    image: landingPageData?.aboutSection?.image,
  };

  const serviceSectionData = {
    title: landingPageData?.serviceSection?.title ?? '',
    subtitle: landingPageData?.serviceSection?.subtitle ?? '',
    backgroundImage: landingPageData?.serviceSection?.backgroundImage,
    services:
      landingPageData?.serviceSection?.services?.map(service => ({
        id: service.id, // Keep as number to match backend
        title: service.title,
        description: service.description,
        image: service.image,
        buttonText: service.buttonText,
        buttonLink: service.buttonLink,
        order: service.order,
      })) ?? [],
    cta: landingPageData?.serviceSection?.cta,
  };

  const partnersSectionData = {
    id: landingPageData?.partnersSection?.id ?? 1,
    title: landingPageData?.partnersSection?.title ?? '',
    subtitle: landingPageData?.partnersSection?.subtitle ?? '',
    partners: landingPageData?.partnersSection?.partners ?? [],
    autoPlay: landingPageData?.partnersSection?.autoPlay ?? false,
    autoPlayInterval:
      landingPageData?.partnersSection?.autoPlayInterval ?? 5000,
  };

  const featuredProjectsSectionData = {
    id: landingPageData?.featuredProjectsSection?.id ?? 1,
    title: landingPageData?.featuredProjectsSection?.title ?? '',
    projects:
      landingPageData?.featuredProjectsSection?.projects?.map(project => ({
        id: project.id,
        title: project.title,
        description: project.description,
        image: project.image,
        order: project.order,
        isActive: project.isActive,
      })) ?? [],
  };

  const featuredNewsSectionData = {
    id: landingPageData?.featuredNewsSection?.id ?? 1,
    title: landingPageData?.featuredNewsSection?.title ?? '',
    subtitle: landingPageData?.featuredNewsSection?.subtitle ?? '',
    viewMoreText:
      landingPageData?.featuredNewsSection?.viewMoreText ?? 'Xem thêm',
    viewMoreLink: landingPageData?.featuredNewsSection?.viewMoreLink ?? '/news',
    articles:
      landingPageData?.featuredNewsSection?.articles?.map(article => ({
        id: article.id,
        documentId: article.documentId,
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt,
        content: article.content,
        author: article.author,
        publishedDate: article.publishedDate,
        readingTime: article.readingTime,
        featured: article.featured,
        tags: article.tags,
        createdAt: article.createdAt,
        updatedAt: article.updatedAt,
        publishedAt: article.publishedAt,
        locale: article.locale,
        featuredImage: article.featuredImage,
      })) ?? [],
  };

  return {
    landingPageData,
    loading,
    error,
    siteData,
    seoData,
    headerData,
    footerData,
    bannerCollectionData,
    aboutSectionData,
    serviceSectionData,
    partnersSectionData,
    featuredProjectsSectionData,
    featuredNewsSectionData,
    refetch: fetchData,
  };
}
